import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { useMemo, useState } from 'react';

import { Radius, Sizes, Spacing, Typography } from 'chatie-design-system';
import {
  Comment,
  Heart,
  HeartFill,
  Icon,
  PinFill,
  VerticalEllipsis,
} from 'chatie-design-system/icon';

import ActionSheet, { ActionSheetOption } from '@components/common/ActionSheet';

import { ICommunityPost } from '../../model';
import { formatDate } from '../../utils/date';
import { trimStringByLine } from '../../utils/misc';
import Avatar, { AvatarSize } from '../common/Avatar';
import EFinderImage from '../common/EFinderImage';
import Nickname from '../user/Nickname';

const postCSS = css`
  position: relative;
`;

const postFixedCSS = (theme: Theme) => css`
  ${Typography.body12};
  color: ${theme.var.gray600};
`;

const postUserCSS = (theme: Theme) =>
  css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${Spacing.medium}px;

    .profile {
      display: flex;
      align-items: center;
    }

    .nickname {
      margin-left: ${Spacing.xsmall}px;

      .fixedContainer {
        display: flex;
        align-items: center;
        gap: ${Spacing.xxsmall}px;
      }

      small {
        display: block;
        margin-top: ${Spacing.xxsmall}px;
        color: ${theme.var.gray500};
        ${Typography.body12};
      }
    }
  `;

const postContentCSS = css`
  ${Typography.body14}
`;

const postContentMoreBtnCSS = (theme: Theme) => css`
  color: ${theme.var.gray500};

  &:before {
    content: '\\00a0';
  }
`;

const postPictureCSS = (isClip: boolean) => css`
  margin-top: ${Spacing.small}px;
  position: relative;
  ${!isClip
    ? css`
        & > span {
          position: unset !important;
          & > img {
            position: unset !important;
            height: auto !important;
            object-fit: contain !important;
          }
        }
      `
    : css`
        padding-bottom: 60%;
      `}
`;

const postButtonCSS = (theme: Theme) => css`
  margin-top: ${Spacing.xsmall}px;
  color: ${theme.var.gray500};

  button {
    ${Typography.body12};
  }

  button + button {
    margin-left: ${Spacing.xsmall}px;
  }
`;

export type PostCardAction = 'modify' | 'delete' | 'report';

export type PostProps = {
  clip?: boolean;
  isMyPage?: boolean;
  onLike?: (post: ICommunityPost) => void;
  handleActionSheet?: (value: PostCardAction, id: number) => void;
} & ICommunityPost;

const Post = ({
  clip: isClip = false,
  onLike,
  isMyPage,
  handleActionSheet,
  ..._post
}: PostProps) => {
  const post = _post as ICommunityPost;
  const {
    id,
    user,
    content,
    picture,
    likeCount,
    commentCount,
    isFixed,
    isLiked,
    created,
  } = post;

  const { t } = useTranslation();
  const theme = useTheme();

  const [isContentOpen, setContentOpen] = useState(false);
  const [content1, content2] = useMemo(
    () => trimStringByLine(content),
    [content]
  );

  const [actions] = useState<ActionSheetOption<PostCardAction>[]>(
    isMyPage
      ? ([
          { label: t('수정하기'), value: 'modify' },
          { label: t('삭제하기'), value: 'delete', isDanger: true },
        ] as ActionSheetOption<PostCardAction>[])
      : [{ label: t('신고하기'), value: 'report' }]
  );

  return (
    <article css={postCSS}>
      <div css={postUserCSS}>
        <div className="profile">
          <Avatar size={AvatarSize.medium} {...user} />
          <div className="nickname">
            <div className="fixedContainer">
              <h6>
                <Nickname {...user} />
              </h6>
              {isFixed && (
                <p css={postFixedCSS} className="icon-text">
                  <Icon
                    icon={PinFill}
                    size={Sizes.small}
                    color={theme.var.gray600}
                  />
                  <span>{t('고정됨')}</span>
                </p>
              )}
            </div>
            <small>{formatDate(created)}</small>
          </div>
        </div>
        {handleActionSheet && (
          <ActionSheet
            name={`post-card-action-${post.id}`}
            options={actions}
            onSelect={(value) => handleActionSheet(value, id)}
          >
            {(open) => (
              <button className="icon-btn" onClick={open}>
                <Icon
                  icon={VerticalEllipsis}
                  color={theme.var.gray400}
                  size={Sizes.small}
                />
              </button>
            )}
          </ActionSheet>
        )}
      </div>

      {isClip ? (
        <p css={postContentCSS} className="multiline">
          {content1}
          {isContentOpen && content2}
          {!isContentOpen && content2 && (
            <button
              css={postContentMoreBtnCSS}
              className="text-btn"
              onClick={(e) => {
                e.stopPropagation();
                setContentOpen(true);
              }}
            >
              ...{t('더보기')}
            </button>
          )}
        </p>
      ) : (
        <p css={postContentCSS} className="multiline">
          {content}
        </p>
      )}
      {picture && picture.key && (
        <div css={postPictureCSS(isClip)}>
          <EFinderImage image={picture} radius={Radius.medium} />
        </div>
      )}

      <div css={postButtonCSS}>
        <button
          className="text-btn icon-text"
          onClick={(e) => {
            e.stopPropagation();
            onLike?.(post);
          }}
        >
          <Icon
            icon={isLiked ? HeartFill : Heart}
            size={Sizes.small}
            color={isLiked ? theme.colors.red500 : theme.var.gray500}
          />
          <span>{likeCount || t('좋아요')}</span>
        </button>
        <button className="text-btn icon-text">
          <Icon icon={Comment} size={Sizes.small} color={theme.var.gray500} />
          <span>{commentCount || t('댓글달기')}</span>
        </button>
      </div>
    </article>
  );
};

export default Post;
